import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { ReactTags } from 'react-tag-autocomplete';
import { Dropdown } from 'flowbite-react';
import { periodOptions } from '../../config/trendsConfig';
import { getPresetDateRange } from '../../hooks/useTrendsData';
import SearchableDropdown from '../common/SearchableDropdown';

const PeriodControls = ({
  selectedPeriod,
  setSelectedPeriod,
  setPreviousPeriod,
  customDateRange,
  setCustomDateRange,
  selectedCountries,
  setSelectedCountries,
  countries,
  selectedClient,
  setSelectedClient,
  profiles,
  isAdmin,
  manualAggregation,
  setManualAggregation,
  currentAggregation,
  currentDateRange
}) => {
  // Local state for countries filter before applying
  const [tempSelectedCountries, setTempSelectedCountries] = useState([]);
  // Move filter visibility state to this component
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Initialize tempSelectedCountries when selectedCountries changes
  useEffect(() => {
    setTempSelectedCountries(selectedCountries);
  }, [selectedCountries]);

  // Local state for client filter before applying
  const [tempSelectedClient, setTempSelectedClient] = useState('');

  // Sync temp client when selectedClient changes
  useEffect(() => {
    setTempSelectedClient(selectedClient);
  }, [selectedClient]);

  // Transform profiles for SearchableDropdown
  const clientItems = profiles ? profiles.map(profile => ({
    label: profile.name,
    value: profile.clientKey
  })) : [];

  const handleApplyFilter = () => {
    setSelectedCountries(tempSelectedCountries);
    setSelectedClient(tempSelectedClient);
  };

  const handleResetFilter = () => {
    setTempSelectedCountries([]);
    setSelectedCountries([]);
    setTempSelectedClient('');
    setSelectedClient('');
  };

  // Format date range for display
  const formatDateRange = (dateRange) => {
    if (!dateRange || !dateRange.fromDate || !dateRange.toDate) return '';

    const formatDate = (date) => {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    return `${formatDate(dateRange.fromDate)} - ${formatDate(dateRange.toDate)}`;
  };
  return (
    <div className="mb-6">
      <div className="flex flex-col space-y-4">
        {/* Controls Row */}
        <div className="flex flex-wrap items-start gap-4">
          {/* Period Section - includes dropdown and date range */}
          <div className="flex flex-col" data-intro="period-controls">
            <div className="flex items-center space-x-2 mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Period:
              </label>
              <div className="relative">
                <Dropdown
                  label={periodOptions.find(option => option.value === selectedPeriod)?.label || 'Select Period'}
                  color="light"
                  size="sm"
                  className="[&>div[data-testid='flowbite-dropdown']]:left-0"
                >
                {periodOptions.map((option) => (
                  <Dropdown.Item
                    key={option.value}
                    onClick={() => {
                      if (option.value === 'CUSTOM') {
                        const previousRange = getPresetDateRange(selectedPeriod);
                        setCustomDateRange(previousRange);
                        // Store the previous period (but not if it was already Custom)
                        if (selectedPeriod !== 'CUSTOM') {
                          setPreviousPeriod(selectedPeriod);
                        }
                      } else {
                        setCustomDateRange({ fromDate: null, toDate: null });
                        // Store the previous period when switching away from Custom
                        if (selectedPeriod === 'CUSTOM') {
                          setPreviousPeriod(option.value);
                        }
                      }
                      setSelectedPeriod(option.value);
                    }}
                    className={selectedPeriod === option.value ? 'bg-blue-50 dark:bg-blue-900' : ''}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
                </Dropdown>
              </div>
            </div>

            {/* Date Range Display - Centered under dropdown */}
            {currentDateRange && (
              <div className="flex justify-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDateRange(currentDateRange)}
                </span>
              </div>
            )}
          </div>

          {/* Aggregation Filter */}
          <div className="flex items-center space-x-2" data-intro="aggregation-filter">
            <label className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
              Aggregation:
            </label>
            <div className="relative">
              <Dropdown
                label={currentAggregation.charAt(0).toUpperCase() + currentAggregation.slice(1)}
                color="light"
                size="sm"
                className="[&>div[data-testid='flowbite-dropdown']]:left-0"
              >
              <Dropdown.Item onClick={() => setManualAggregation('daily')}>
                Daily
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setManualAggregation('weekly')}>
                Weekly
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setManualAggregation('monthly')}>
                Monthly
              </Dropdown.Item>
            </Dropdown>
            </div>
            {manualAggregation && (
              <button
                onClick={() => setManualAggregation(null)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                title="Reset to default aggregation"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {selectedPeriod === 'CUSTOM' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Date
              </label>
              <DatePicker
                selected={customDateRange.fromDate}
                onChange={(date) => {
                  setCustomDateRange(prev => ({ ...prev, fromDate: date }));
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select from date"
                maxDate={customDateRange.toDate || new Date()}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                wrapperClassName="w-full"
                calendarClassName="themed-datepicker-calendar"
                popperClassName="themed-datepicker-popper"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To Date
              </label>
              <DatePicker
                selected={customDateRange.toDate}
                onChange={(date) => {
                  setCustomDateRange(prev => ({ ...prev, toDate: date }));
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select to date"
                minDate={customDateRange.fromDate}
                maxDate={new Date()}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                wrapperClassName="w-full"
                calendarClassName="themed-datepicker-calendar"
                popperClassName="themed-datepicker-popper"
              />
            </div>
          </div>
        )}

        {((countries && countries.length > 1) || (isAdmin && profiles && profiles.length > 0)) && (
          <div className="pt-4">
            <button
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className="flex items-center space-x-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
              data-intro="filter-options"
            >
              <svg
                className={`w-4 h-4 transition-transform ${
                  showAdvancedOptions ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span>{showAdvancedOptions ? 'Hide Filter' : 'Show Filter'}</span>
            </button>

            <div
              className={`bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out ${
                showAdvancedOptions
                  ? 'max-h-96 opacity-100 mt-4 p-4 overflow-visible'
                  : 'max-h-0 opacity-0 p-0 overflow-hidden'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Client Filter - Admin Only */}
                {isAdmin && profiles && profiles.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Client
                    </label>
                    <SearchableDropdown
                      items={clientItems}
                      onSelect={(selectedItem) => setTempSelectedClient(selectedItem.value)}
                      onClear={() => setTempSelectedClient('')}
                      buttonPlaceholder="Select client"
                      searchPlaceholder="Search clients..."
                      widthClass="w-full"
                      selectedValue={tempSelectedClient}
                    />
                  </div>
                )}

                {/* Countries Filter */}
                {countries && countries.length > 1 && (
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Countries
                  </label>
                  <ReactTags
                    selected={tempSelectedCountries.map(country => ({
                      value: country.id,
                      label: country.name
                    }))}
                    suggestions={countries.map(country => ({
                      value: country.id,
                      label: country.name
                    }))}
                    onAdd={(newTag) => {
                      const countryObj = countries.find(c => c.id === newTag.value);
                      if (countryObj) {
                        const isAlreadySelected = tempSelectedCountries.some(c => c.id === countryObj.id);
                        if (!isAlreadySelected) {
                          setTempSelectedCountries([...tempSelectedCountries, countryObj]);
                        }
                      }
                    }}
                    onDelete={(tagIndex) => {
                      const newCountries = tempSelectedCountries.filter((_, index) => index !== tagIndex);
                      setTempSelectedCountries(newCountries);
                    }}
                    allowNew={false}
                    placeholderText="Select countries..."
                  />
                </div>
                )}
              </div>

              {/* Filter Action Buttons */}
              <div className="mt-4 flex justify-center space-x-3">
                <button
                  onClick={handleApplyFilter}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span>Filter</span>
                </button>
                <button
                  onClick={handleResetFilter}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Clear</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodControls;
